import axios, { AxiosRequestHeaders } from 'axios';
import { AddInStorage } from '../storage/AddInStorage';
import { LocalStorageKey } from '../util-helpers/common';
import { ENV_SETTINGS, getEnvironment, userManager } from './oidcClient';

let cachedClientIP: string | null = null;
let ipFetchPromise: Promise<string> | null = null;

// interfaces
export interface IUserOrg {
  name: string;
  orgId: number;
  pdfEditEnabled: boolean;
}

export interface IUserOrgOptions {
  label: string;
  value: number;
}

// Create a simple HTTP client for userSession's own API calls
// This avoids circular dependencies since it doesn't need the full auth interceptor
// Can optionally include a token for authenticated requests
const createSessionHttpClient = (token?: string) => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-fv-application': 'outlook-addin',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return axios.create({
    baseURL: getApiBase(),
    timeout: 30000,
    headers,
  });
};

// auth helpers
const getClientIP = async () => {
  if (cachedClientIP) return cachedClientIP;

  if (ipFetchPromise) return ipFetchPromise;

  ipFetchPromise = (async (): Promise<string> => {
    try {
      const response = await fetch('https://api.ipify.org/?format=json');
      const data = await response.json();
      cachedClientIP = data.ip;
      return cachedClientIP!;
    } catch (e) {
      console.log('Failed to fetch client IP:', e);
      cachedClientIP = "127.0.0.1";
      return cachedClientIP;
    } finally {
      ipFetchPromise = null;
    }
  })();

  return ipFetchPromise;
};

export const getCachedClientIP = (): string | null => cachedClientIP;
export const initializeClientIP = async (): Promise<string> => getClientIP();

export const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  return user?.access_token || null;
};

export const getApiBase = () => {
  const environment = getEnvironment();

  if (!environment) {
    throw new Error('No environment detected');
  }

  return ENV_SETTINGS[environment].API_BASE;
};

export const getUserOrgsAndUserID = async (accessToken?: string | null) => {
  const token = accessToken || (await getAccessToken());

  if (!token) {
    return null;
  }

  const httpClient = createSessionHttpClient(token);
  const request = await httpClient.post('/utils/GetUserOrgsWithToken', null);

  return {
    orgs: request.data.orgs,
    ID: request.data.user.userId.native,
  };
};
export const generateHeaders = async (includeUserIDandOrg = false):
  Promise<AxiosRequestHeaders> => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    return {};
  }

  const headers: AxiosRequestHeaders = {
    Authorization: `Bearer ${accessToken}`,
    "x-fv-clientip": await getClientIP()
  };

  if (includeUserIDandOrg) {
    const userIdAndOrgs = await getUserOrgsAndUserID(accessToken);
    if (userIdAndOrgs?.orgs?.length && userIdAndOrgs.orgs[0]?.orgId && userIdAndOrgs?.ID) {
      setUserId(userIdAndOrgs.ID);
      setUserOrgs(userIdAndOrgs.orgs);
      setOrgId(userIdAndOrgs.orgs[0].orgId);
      headers["x-fv-orgid"] = userIdAndOrgs.orgs[0].orgId;
      headers["x-fv-userid"] = userIdAndOrgs.ID;
    }
  }
  return headers;
};

// userId
export const getUserId = (): string => (
  AddInStorage.getItem(LocalStorageKey.UserId) || ''
);
export const setUserId = (userId: number | string) => (
  AddInStorage.setItem(LocalStorageKey.UserId, userId)
);

// userOrgs
export const getUserOrgs = (): IUserOrg[] => {
  const orgs = AddInStorage.getItem(LocalStorageKey.UserOrgs);
  return orgs || [];
};
export const setUserOrgs = (userOrgs: IUserOrg[]) => (
  AddInStorage.setItem(LocalStorageKey.UserOrgs, userOrgs)
);

// userOrgsAsOptions
export const getUserOrgsAsOptions = (): IUserOrgOptions[] => getUserOrgs()?.map(
  (o) => ({ label: o.name, value: o.orgId }),
);

// orgId
export const getOrgId = (): number | null => {
  const id = AddInStorage.getItem(LocalStorageKey.OrgId);
  return id ? +id : null;
};
export const setOrgId = (orgId: number | string) => {
  AddInStorage.setItem(LocalStorageKey.OrgId, orgId);
};

// currentOrg
export const getCurrentOrg = (): IUserOrg | undefined => {
  const orgId = getOrgId();
  return getUserOrgs().find((o) => o.orgId === orgId);
};

// ephemeralId
export const getEphemeralId = (): string => process.env.REACT_APP_EPHEMERAL_ID || '';
